import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { CloudinaryUploader } from "@/components/CloudinaryUploader";
import { SecondaryButton } from "./AdminUI";
import { ArrowDown, ArrowUp, ImagePlus, X } from "lucide-react";

/**
 * Editable gallery for a brand's "Our take" images. Each image has an
 * editable URL and an optional clothing-item link (only items belonging
 * to this brand are eligible).
 *
 * Props:
 *  - brandId: string (used to scope the clothing-item dropdown)
 *  - value: [{url, clothing_item_id?}]
 *  - onChange: (next) => void
 */
export function OurTakeImagesField({ brandId, value, onChange }) {
  const items = value || [];

  const { data: brandClothing = [] } = useQuery({
    queryKey: ["our-take-brand-clothing", brandId],
    queryFn: async () => (await api.get("/admin/clothing-items", { params: { brand_id: brandId } })).data,
    enabled: !!brandId,
  });

  function update(idx, patch) {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function remove(idx) {
    onChange(items.filter((_, i) => i !== idx));
  }
  function move(idx, delta) {
    const next = items.slice();
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }
  function addByUrl() {
    const url = window.prompt("Image URL");
    if (url && url.startsWith("http")) onChange([...items, { url, clothing_item_id: null }]);
  }
  function addUploaded(url) {
    onChange([...items, { url, clothing_item_id: null }]);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">Gallery (optional)</h3>
        <div className="flex items-center gap-2">
          <CloudinaryUploader
            mode="admin"
            folder="sustainably_curated/personal_images"
            multiple={true}
            label="Upload"
            onUploaded={addUploaded}
          />
          <SecondaryButton onClick={addByUrl} data-testid="our-take-image-add-url">
            <ImagePlus className="w-3.5 h-3.5 inline mr-1" /> Add via URL
          </SecondaryButton>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[#6C757D] italic">No images yet. Add product shots, brand-store photos, etc.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-start gap-2 border border-[#E9ECEF] rounded-md p-2 bg-white" data-testid={`our-take-image-${i}`}>
              <div className="w-20 h-20 shrink-0 bg-[#F1F3F5] rounded overflow-hidden flex items-center justify-center">
                {it.url ? (
                  <img src={it.url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImagePlus className="w-5 h-5 text-[#6C757D]" />
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                <input
                  className="w-full text-xs border border-[#E9ECEF] rounded px-2 py-1 font-mono"
                  value={it.url || ""}
                  onChange={(e) => update(i, { url: e.target.value })}
                  placeholder="Image URL"
                  data-testid={`our-take-image-url-${i}`}
                />
                <div className="flex items-center gap-2">
                  <label className="text-[11px] uppercase tracking-wider text-[#6C757D] shrink-0">Links to</label>
                  <select
                    className="flex-1 text-xs border border-[#E9ECEF] rounded px-2 py-1 bg-white"
                    value={it.clothing_item_id || ""}
                    onChange={(e) => update(i, { clothing_item_id: e.target.value || null })}
                    data-testid={`our-take-image-link-${i}`}
                  >
                    <option value="">— No link (just an image)</option>
                    {brandClothing.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs p-1 text-[#6C757D] disabled:opacity-30" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-xs p-1 text-[#6C757D] disabled:opacity-30" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(i)} className="text-xs p-1 text-clay" title="Remove"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
